/**
 * PinterestBoardsChips — sous-composant `<PlatformConnectionCard>` qui
 * affiche la liste des boards Pinterest synchronisés sous forme de chips
 * + un bouton "Rafraîchir mes boards" (re-sync depuis pinterest.com).
 *
 * Affiche aussi un compte "X boards • dernière sync il y a Y" calculé
 * depuis le `last_synced_at` du premier board (tous les boards d'une
 * connexion partagent le même timestamp via le pattern de sync atomique
 * de `pinterest_boards_queries.upsert_boards`).
 *
 * Si la connexion n'a pas encore de boards (ex: sync échoué post-OAuth),
 * affiche juste le bouton "Rafraîchir" sans liste — Stéphanie peut
 * relancer la sync manuellement.
 *
 * Verrous tenus :
 *   - Pas de hex (tokens DS via classes).
 *   - Pas d'inline static styles.
 *   - Pas de `<button>` natif → `<Button>` primitive.
 */

import { Loader2, RotateCw } from 'lucide-react'
import { toast } from 'sonner'

import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import { usePinterestBoards, useRefreshPinterestBoards } from '@/hooks/usePlatformConnections'

import './PinterestBoardsChips.css'

interface PinterestBoardsChipsProps {
  connectionId: number
}

export default function PinterestBoardsChips({ connectionId }: PinterestBoardsChipsProps) {
  const boardsQuery = usePinterestBoards(connectionId)
  const refreshMutation = useRefreshPinterestBoards()

  const handleRefresh = async () => {
    try {
      await refreshMutation.mutateAsync(connectionId)
      toast.success('Boards Pinterest mis à jour.')
    } catch (err) {
      const detail =
        err && typeof err === 'object' && 'detail' in err
          ? String((err as { detail: unknown }).detail)
          : 'Sync Pinterest impossible.'
      toast.error(detail)
    }
  }

  const boards = boardsQuery.data ?? []
  const lastSyncedAt = boards[0]?.last_synced_at ?? null

  return (
    <section className="pinterest-boards-chips" aria-label="Boards Pinterest synchronisés">
      <header className="pinterest-boards-chips-header">
        <div>
          <p className="ui-eyebrow pinterest-boards-chips-eyebrow">Boards</p>
          <p className="ui-body ui-text-muted pinterest-boards-chips-summary">
            {boardsQuery.isLoading
              ? 'Chargement des boards…'
              : boards.length === 0
                ? 'Aucun board synchronisé pour le moment.'
                : `${boards.length} board${boards.length > 1 ? 's' : ''} • dernière sync. ${formatRelative(lastSyncedAt)}`}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshMutation.isPending}
        >
          {refreshMutation.isPending ? (
            <Loader2 aria-hidden="true" size={14} className="platform-spinner" />
          ) : (
            <RotateCw aria-hidden="true" size={14} />
          )}
          Rafraîchir
        </Button>
      </header>

      {boards.length > 0 ? (
        <ul className="pinterest-boards-chips-list" aria-label="Liste des boards">
          {boards.map((board) => (
            <li key={board.external_board_id} className="pinterest-boards-chips-item">
              <span className="pinterest-boards-chips-name">{board.name}</span>
              {board.privacy === 'secret' ? (
                <Badge variant="warning" size="sm" title="Board privé — Pin invisible publiquement">
                  Secret
                </Badge>
              ) : board.privacy === 'protected' ? (
                <Badge
                  variant="default"
                  size="sm"
                  title="Board protégé — visible uniquement par les invités"
                >
                  Protégé
                </Badge>
              ) : null}
              {typeof board.pin_count === 'number' ? (
                <span className="pinterest-boards-chips-count">
                  {board.pin_count} pin{board.pin_count > 1 ? 's' : ''}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}

function formatRelative(iso: string | null): string {
  if (!iso) return '—'
  const t = new Date(iso).getTime()
  const diffSec = Math.max(0, Math.round((Date.now() - t) / 1000))
  if (diffSec < 60) return 'à l’instant'
  if (diffSec < 3600) return `il y a ${Math.floor(diffSec / 60)} min`
  if (diffSec < 86400) return `il y a ${Math.floor(diffSec / 3600)} h`
  const days = Math.floor(diffSec / 86400)
  return `il y a ${days} j`
}
